import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { useTitleStore } from 'src/stores/useTitleStore';
import * as React from 'react';
import { Button } from 'src/registry/ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { booleanBadgeOptions, ICON_MAP } from 'src/utils/constants';
import { SectionBlock, SectionBlockHeader, SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import { Badge } from 'src/registry/ui/Badge';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { commonMessages, generalMessages } from 'src/utils/messages';
import DashboardGeneralBlock from 'src/features/billing/components/DashboardGeneralBlock';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';
import { discountPoliciesMessages } from 'src/features/discountPolicies/utils/messages';
import { discountsMessages } from 'src/features/discounts/utils/messages';
import { useGetDiscount } from 'src/features/discounts/api/useGetDiscount';
import { vouchersMessages } from 'src/features/vouchers/utils/messages';
import { subscriptionsMessages } from 'src/features/subscriptions/utils/messages';
import DeleteDiscountFormModal from 'src/features/discounts/components/forms/DeleteDiscountFormModal';
import ChargesTable from 'src/features/charges/components/tables/ChargesTable';
import { subscriptionItemsMessages } from 'src/features/subscriptionItems/utils/messages';
import { customersMessages } from 'src/features/customers/utils/messages';
import { CellCurrencyBadge } from 'src/features/moneyAccounts/components/tables/CellCurrencyBadge';
import { pricingPoliciesMessages } from 'src/features/servicePlanPricingPolicies/utils/messages';
import UpdateDiscountFormModal from 'src/features/discounts/components/forms/UpdateDiscountFormModal';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';
import { LinkWithIcon } from 'src/registry/components/navigation/LinkWithIcon';

const DiscountDashboard = () => {
    const { formatMessage, formatNumber } = useIntl();
    const navigate = useNavigate();
    const { discountId = '' } = useParams();

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(discountsMessages.discountDashboard));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { data: discount } = useGetDiscount({
        discountId: discountId,
        config: { enabled: !!discountId },
    });

    const permissionManageDiscounts = useHasPermissions([PERMISSION.MANAGE_DISCOUNTS]);
    const permissionAccessCharges = useHasPermissions([PERMISSION.ACCESS_CHARGES]);
    const permissionAccessCustomers = useHasPermissions([PERMISSION.ACCESS_CUSTOMERS]);
    const permissionAccessSubscriptions = useHasPermissions([PERMISSION.ACCESS_SUBSCRIPTIONS]);

    const isUsedAsString = discount?.is_used ? 'true' : 'false';

    const dashboardBlockData = {
        section1: [
            {
                title: discountsMessages.startDate,
                value: getFormattedDateTimeForDetailPanel(discount?.start_date) ?? '-',
            },
            {
                title: discountsMessages.endDate,
                value: discount?.end_date ? getFormattedDateTimeForDetailPanel(discount?.end_date) : '-',
            },
            {
                title: vouchersMessages.isUsed,
                value: discount ? (
                    <Badge theme={booleanBadgeOptions[isUsedAsString].theme || 'gray'}>
                        <FontAwesomeIcon icon={booleanBadgeOptions[isUsedAsString].icon as IconProp} />
                        {formatMessage(booleanBadgeOptions[isUsedAsString].label)}
                    </Badge>
                ) : (
                    '-'
                ),
            },
            {
                title: commonMessages.currency,
                value: discount?.currency ? <CellCurrencyBadge currency={discount?.currency} /> : '-',
            },
            {
                title: pricingPoliciesMessages.amount,
                value:
                    discount?.discount_policy?.amount && discount.currency
                        ? formatNumber(discount?.discount_policy?.amount / 100, {
                              style: 'currency',
                              currency: discount.currency,
                          })
                        : '-',
            },
            {
                title: discountPoliciesMessages.percent,
                value: discount?.discount_policy?.percent
                    ? `${(Number(discount?.discount_policy.percent) * 100).toFixed(2)}%`
                    : '-',
            },
        ],
        section2: [
            {
                title: discountPoliciesMessages.discountPolicy,
                value: discount?.discount_policy && (
                    <LinkWithIcon
                        to={`/discount-policies/${discount.discount_policy.id}`}
                        linkTitle={discount.discount_policy.name}
                        hasPermissions={true}
                    />
                ),
            },
            {
                title: subscriptionsMessages.subscription,
                value: discount?.subscription && (
                    <LinkWithIcon
                        to={`/subscriptions/${discount.subscription.id}`}
                        linkTitle={`${discount.subscription.reference} - ${discount.subscription.service_plan?.name}`}
                        hasPermissions={permissionAccessSubscriptions}
                    />
                ),
            },
            {
                title: subscriptionItemsMessages.subscriptionItems,
                value: discount?.subscription_item && (
                    <LinkWithIcon
                        to={`/subscriptions/${discount.subscription.id}/subscription-item/${discount.subscription_item?.id}`}
                        linkTitle={discount?.subscription_item.service_plan_item?.name}
                        hasPermissions={permissionAccessSubscriptions}
                    />
                ),
            },
            {
                title: customersMessages.customer,
                value: discount?.customer && (
                    <LinkWithIcon
                        to={`/customers/${discount?.customer?.id}`}
                        linkTitle={discount?.customer?.name}
                        hasPermissions={permissionAccessCustomers}
                    />
                ),
            },
        ],
    };

    return (
        <>
            <Button asChild className="place-self-start p-0" variant="link" onClick={() => navigate('/discounts')}>
                <div className="flex cursor-pointer">
                    <FontAwesomeIcon icon={ICON_MAP.BACK} className="opacity-50" />
                    {formatMessage(discountsMessages.backToAllDiscounts)}
                </div>
            </Button>

            <div className="mt-6 grid grid-cols-1 gap-8">
                <SectionBlock>
                    <SectionBlockHeader>
                        <SectionBlockTitle className="text-2xl whitespace-nowrap">
                            {`${discount?.discount_policy.name} - ${discount?.subscription?.service_plan?.name}`}
                        </SectionBlockTitle>
                        {permissionManageDiscounts && discount && (
                            <div className="flex flex-row gap-2">
                                <UpdateDiscountFormModal
                                    semantics={{ entryName: formatMessage(discountsMessages.discount).toLowerCase() }}
                                    selectedDiscount={discount}
                                >
                                    <Button size="mdIcon" variant="outline" className="cursor-pointer">
                                        <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                        <span className="hidden md:block">{formatMessage(generalMessages.edit)}</span>
                                    </Button>
                                </UpdateDiscountFormModal>
                                <DeleteDiscountFormModal
                                    selectedDiscount={discount}
                                    semantics={{
                                        entryName: formatMessage(discountsMessages.discount).toLowerCase(),
                                    }}
                                >
                                    <Button size="mdIcon" variant="destructiveLight" className="cursor-pointer">
                                        <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                        <span className="hidden md:block">{formatMessage(generalMessages.delete)}</span>
                                    </Button>
                                </DeleteDiscountFormModal>
                            </div>
                        )}
                    </SectionBlockHeader>
                    <DashboardGeneralBlock dashboardData={dashboardBlockData} indexForLastSeparator={1} />
                </SectionBlock>
                {/* A single table needs no tab row — a one-entry row is a control that cannot act. */}
                {permissionAccessCharges && (
                    <div className="grid flex-3 grid-cols-1">
                        <ChargesTable />
                    </div>
                )}
            </div>
        </>
    );
};

export default DiscountDashboard;
